import React from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon } from '@heroicons/react/24/outline';
import { GRID_CONFIG } from '../gridConfig';
import { CollapseIcon } from '../../components_misc/CollapseIcon';
import { ExpandIcon } from '../../components_misc/ExpandIcon';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { MapTitleBar } from './MapTitleBar';
import { isStatusColumn } from '../utils/layoutMath';
import type { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import type { Objective1 } from '@mm-shared/types/Objective1';
import type { ColumnInfo } from '../utils/layoutMath';
import type { RelativesMapData } from './Map3Relatives';

/**
 * ARCHITECTURAL INSIGHT: RENDERING OUTSIDE REACT FLOW
 * ===================================================
 *
 * This component uses a hybrid approach for rendering thick lines and wrappers
 * for fully collapsed maps. Instead of rendering them as React Flow nodes (which
 * would be subject to React Flow's internal z-index management), we render them
 * as regular React elements positioned absolutely OUTSIDE the React Flow canvas.
 *
 * KEY BENEFITS:
 * - Complete z-index control without React Flow interference
 * - Better performance (no React Flow node overhead for static elements)
 * - Simpler hover/interaction management
 * - No conflicts with React Flow's internal layering system
 *
 * IMPLEMENTATION LOCATIONS:
 * - Search for "RENDERING OUTSIDE REACT FLOW STRATEGY" comments in this file
 * - Stacked version: ~line 1150
 * - Shared timeline version: ~line 1900
 *
 * REFERENCES:
 * - React Flow Troubleshooting: https://reactflow.dev/learn/troubleshooting
 * - React Flow Core Concepts: https://reactflow.dev/learn/concepts/core-concepts
 */

/**
 * User-controllable collapse properties
 */
export interface UserMapCollapseControls {
  collapseLevel?: 'expanded' | 'titleOnly' | 'fullyCollapsed';
  hideRowsWithoutDependencies?: boolean;
}

/**
 * FULLY COLLAPSED RENDERING - EXTERNAL RENDERING STRATEGY
 * =======================================================
 *
 * These components implement the "external rendering" strategy for fully collapsed maps.
 * Instead of rendering thick lines as React Flow nodes (which would be subject to React Flow's
 * internal z-index management), we render them as regular React elements positioned absolutely
 * OUTSIDE the React Flow canvas.
 *
 * WHY THIS APPROACH IS NECESSARY:
 *
 * 1. Z-INDEX CONFLICTS: React Flow manages its own internal z-index hierarchy
 *    (https://reactflow.dev/learn/troubleshooting/z-index-issues)
 *    - Nodes: z-index 1000-2000+ (auto-managed)
 *    - Edges: z-index 1500+ (auto-managed)
 *    - Selection box: z-index 100+ (auto-managed)
 *
 * 2. OUR REQUIREMENTS: We need thick lines to appear ABOVE result nodes
 *    but BELOW interactive elements, with precise layering control
 *
 * 3. THE PROBLEM: When rendered as React Flow nodes, our z-index values
 *    get overridden by React Flow's internal z-index management system
 *
 * HOW THIS SOLUTION WORKS:
 *
 * 1. CONTAINER HIERARCHY:
 *    ```
 *    <div> (our container - position: relative)
 *      <ReactFlow> (canvas - position: relative, z-index lower)
 *        {nodes and edges}
 *      </ReactFlow>
 *      <div> (our external elements - position: absolute, z-index higher)
 *        {thick lines and wrappers}
 *      </div>
 *    </div>
 *    ```
 *
 * 2. POSITIONING: We calculate absolute positions that match React Flow's
 *    internal coordinate system, using the same mapXOffset/mapYOffset values
 *
 * 3. Z-INDEX CONTROL: We have complete control over z-index hierarchy:
 *    - Wrapper: z-index 5000 (hides result nodes)
 *    - Thick lines: z-index 6000 (appear above wrapper)
 *    - Hover previews: z-index 7000 (appear above thick lines)
 *
 * BENEFITS:
 * - Complete z-index control without React Flow interference
 * - Better performance (no React Flow node overhead for static elements)
 * - Simpler hover/interaction management
 * - No "disappearing elements" when React Flow updates
 *
 * TRADE-OFFS:
 * - Manual coordinate calculation (not automatic like React Flow nodes)
 * - Elements don't participate in React Flow's zoom/pan (good for our use case)
 * - Need to maintain positioning consistency with React Flow coordinate system
 *
 * REFERENCES:
 * - React Flow Troubleshooting: https://reactflow.dev/learn/troubleshooting
 * - React Flow Core Concepts: https://reactflow.dev/learn/concepts/core-concepts
 * - CSS Stacking Context: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context
 */

export interface FullyCollapsedMapsGroupProps {
  fullyCollapsedMaps: RelativesMapData[];
  hoveredThickLineUuid: string | null;
  setHoveredThickLineUuid: (uuid: string | null) => void;
  updateMapState?: (mapUuid: string, updates: UserMapCollapseControls) => void;
  variant: 'stacked' | 'sharedTimeline';
}

/**
 * Calculates wrapper styles for both stacked and shared timeline variants.
 *
 * POSITIONING STRATEGY:
 *
 * STACKED VIEW:
 * - All maps have same X offset (left-aligned)
 * - Wrapper spans the maximum timeline width of all collapsed maps
 * - Simple horizontal positioning calculation
 *
 * SHARED TIMELINE VIEW:
 * - Maps have different X offsets (aligned to unified timeline)
 * - Wrapper spans from leftmost to rightmost timeline positions
 * - More complex calculation to cover all timeline areas
 *
 * The wrapper serves as a "title bar" background for the entire fullyCollapsed section,
 * hiding result nodes behind it while keeping thick lines visible above.
 */
function calculateWrapperStyle(
  fullyCollapsedMaps: RelativesMapData[],
  variant: 'stacked' | 'sharedTimeline',
) {
  const firstFullyCollapsed = fullyCollapsedMaps[0];

  if (variant === 'stacked') {
    const maxTimelineWidth = Math.max(
      ...fullyCollapsedMaps.map((relative) =>
        relative.columnsInfo
          .filter((col) => isStatusColumn(col))
          .reduce((sum, col) => sum + col.width, 0),
      ),
    );

    return {
      position: 'absolute' as const,
      left: firstFullyCollapsed.mapXOffset + GRID_CONFIG.objectiveWidth,
      top: firstFullyCollapsed.mapYOffset,
      width: `${maxTimelineWidth - 1}px`,
      height: `${GRID_CONFIG.cellHeight}px`,
      backgroundColor: 'white',
      zIndex: 5000,
      border: '1px solid rgb(229, 231, 235)',
      borderRadius: '2px',
    };
  } else {
    // Shared timeline
    const leftmostTimelineStart = Math.min(
      ...fullyCollapsedMaps.map((relative) => relative.mapXOffset + GRID_CONFIG.objectiveWidth),
    );
    const rightmostTimelineEnd = Math.max(
      ...fullyCollapsedMaps.map((relative) => {
        const timelineWidth = relative.columnsInfo
          .filter((col) => isStatusColumn(col))
          .reduce((sum, col) => sum + col.width, 0);
        return relative.mapXOffset + GRID_CONFIG.objectiveWidth + timelineWidth;
      }),
    );

    return {
      position: 'absolute' as const,
      left: leftmostTimelineStart,
      top: firstFullyCollapsed.mapYOffset,
      width: `${rightmostTimelineEnd - leftmostTimelineStart - 1}px`,
      height: `${GRID_CONFIG.cellHeight}px`,
      backgroundColor: 'white',
      zIndex: 5000,
      border: '1px solid rgb(229, 231, 235)',
      borderRadius: '2px',
    };
  }
}

/**
 * Renders the hover preview for a fully collapsed map.
 *
 * HOVER PREVIEW ARCHITECTURE:
 *
 * This component creates an instant preview of what the map would look like
 * in "titleOnly" state when hovering over a thick line. It's positioned
 * directly above the hovered thick line.
 *
 * POSITIONING EXPLANATION:
 * - left: relative.mapXOffset (aligns with map's left edge)
 * - top: firstFullyCollapsed.mapYOffset + lineY - GRID_CONFIG.cellHeight
 *   (positions directly above the thick line)
 * - width: GRID_CONFIG.objectiveWidth + mapTimelineWidth (full map width)
 * - height: GRID_CONFIG.cellHeight (exactly one row tall - title bar only)
 *
 * STRUCTURE MATCHES MAPNODE:
 * The preview replicates the exact structure of MapNode's title bar to ensure
 * visual consistency. This includes:
 * - Objectives column background (white)
 * - Title bar with grid layout (link icon, title, collapse/expand buttons)
 * - Same styling, spacing, and interaction patterns
 *
 * Z-INDEX HIERARCHY:
 * - Preview container: z-index 7000 (highest - above thick lines at 6000)
 * - Internal elements use relative z-index for proper layering
 * - All preview elements appear above the thick line wrapper (5000)
 */
function FullyCollapsedHoverPreview({
  relative,
  lineY,
  firstFullyCollapsed,
  mapTimelineWidth,
  onMouseEnter,
  onMouseLeave,
  updateMapState,
}: {
  relative: RelativesMapData;
  lineY: number;
  firstFullyCollapsed: RelativesMapData;
  mapTimelineWidth: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  updateMapState?: (mapUuid: string, updates: UserMapCollapseControls) => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: relative.mapXOffset,
        top: firstFullyCollapsed.mapYOffset + lineY - GRID_CONFIG.cellHeight,
        width: `${GRID_CONFIG.objectiveWidth + mapTimelineWidth}px`,
        height: `${GRID_CONFIG.cellHeight}px`,
        zIndex: 7000,
        pointerEvents: 'auto',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Objectives Column Background */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: GRID_CONFIG.objectiveWidth,
          height: GRID_CONFIG.cellHeight,
          backgroundColor: 'white',
          zIndex: 0,
        }}
      />

      {/* Title Row - using shared MapTitleBar component */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          zIndex: 1,
        }}
      >
        <MapTitleBar
          milestoneMap={relative.milestoneMap}
          collapseLevel="fullyCollapsed" // Always fullyCollapsed in this context
          isMainMap={false} // Hover previews are never for main map
          onCollapseClick={() => {
            // Already fully collapsed, no action needed
          }}
          onExpandClick={() => {
            if (updateMapState) {
              updateMapState(relative.milestoneMap.uuid, {
                collapseLevel: 'titleOnly',
              });
            }
          }}
          width={GRID_CONFIG.objectiveWidth + mapTimelineWidth}
          height={GRID_CONFIG.cellHeight}
          variant="inline-styles"
          containerZIndex={2}
        />
      </div>
    </div>
  );
}

/**
 * Renders a single thick line with hover preview for a fully collapsed map.
 *
 * THICK LINE DESIGN:
 *
 * Visual Representation:
 * - 4px thick horizontal line spanning the map's timeline width
 * - Gray (#9ca3af) by default, darker gray (#374151) when hovered
 * - Blue glow effect on hover (matches selected edge treatment)
 * - Smooth transitions for visual feedback
 *
 * POSITIONING ALGORITHM:
 * - Horizontal: relative.mapXOffset + GRID_CONFIG.objectiveWidth (start after objectives column)
 * - Vertical: Even spacing within the fullyCollapsed section
 *   - sectionHeight / (totalMaps + 1) creates equal spacing
 *   - lineSpacing * (index + 1) - 2 centers the 4px line
 * - Width: mapTimelineWidth (preserves individual map's timeline span)
 *
 * INTERACTION BEHAVIOR:
 * - Click: Expands map to titleOnly state (fullyCollapsed → titleOnly cycle)
 * - Hover: Shows instant preview above the line + visual feedback
 * - Mouse events: Properly coordinated with hover preview component
 *
 * Z-INDEX POSITIONING:
 * - Thick line: z-index 6000 (above wrapper at 5000, below preview at 7000)
 * - Interactive layer that sits between background wrapper and hover preview
 */
function FullyCollapsedThickLine({
  relative,
  index,
  totalMaps,
  firstFullyCollapsed,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  updateMapState,
}: {
  relative: RelativesMapData;
  index: number;
  totalMaps: number;
  firstFullyCollapsed: RelativesMapData;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  updateMapState?: (mapUuid: string, updates: UserMapCollapseControls) => void;
}) {
  const sectionHeight = GRID_CONFIG.cellHeight;
  const lineSpacing = sectionHeight / (totalMaps + 1);
  const lineY = lineSpacing * (index + 1) - 2;

  const mapTimelineWidth = relative.columnsInfo
    .filter((col) => isStatusColumn(col))
    .reduce((sum, col) => sum + col.width, 0);

  return (
    <React.Fragment key={`thick-line-fragment-${relative.milestoneMap.uuid}`}>
      {/* Thick line */}
      <div
        style={{
          position: 'absolute',
          left: relative.mapXOffset + GRID_CONFIG.objectiveWidth,
          top: firstFullyCollapsed.mapYOffset + lineY,
          width: `${mapTimelineWidth}px`,
          height: '4px',
          backgroundColor: isHovered ? '#374151' : '#9ca3af',
          borderRadius: '2px',
          zIndex: 6000,
          cursor: 'pointer',
          filter: isHovered ? 'drop-shadow(0 0 4px #2563eb)' : 'none',
          transition: 'all 0.2s ease',
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        title={`${relative.milestoneMap.title} (fully collapsed) - Click to expand`}
      />

      {/* Hover preview */}
      {isHovered && (
        <FullyCollapsedHoverPreview
          relative={relative}
          lineY={lineY}
          firstFullyCollapsed={firstFullyCollapsed}
          mapTimelineWidth={mapTimelineWidth}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          updateMapState={updateMapState}
        />
      )}
    </React.Fragment>
  );
}

/**
 * Renders the complete fully collapsed section with wrapper and thick lines.
 *
 * ORCHESTRATION COMPONENT:
 *
 * This is the main orchestrator for the external rendering strategy. It coordinates
 * all elements needed to properly display fully collapsed maps outside React Flow:
 *
 * RENDERING LAYERS (bottom to top):
 * 1. White background wrapper (z-index 5000)
 *    - Hides result nodes behind a clean white background
 *    - Spans appropriate width based on variant (stacked vs shared timeline)
 *
 * 2. Thick lines (z-index 6000)
 *    - Individual 4px lines for each fully collapsed map
 *    - Interactive elements that respond to clicks and hover
 *    - Positioned with even spacing within the section
 *
 * 3. Hover previews (z-index 7000)
 *    - Conditional rendering based on hover state
 *    - Instant preview of titleOnly state above hovered lines
 *
 * COORDINATE CALCULATION:
 * Since we're rendering outside React Flow, we manually calculate absolute positions
 * that align with React Flow's internal coordinate system using the same
 * mapXOffset/mapYOffset values that React Flow nodes use.
 *
 * VARIANT SUPPORT:
 * - Stacked: Simple left-aligned positioning
 * - Shared Timeline: Complex positioning spanning leftmost to rightmost timelines
 *
 * This approach gives us complete z-index control without React Flow interference,
 * better performance, and simpler hover/interaction management.
 */
export function FullyCollapsedMapsGroup({
  fullyCollapsedMaps,
  hoveredThickLineUuid,
  setHoveredThickLineUuid,
  updateMapState,
  variant,
}: FullyCollapsedMapsGroupProps) {
  if (fullyCollapsedMaps.length === 0) return null;

  const firstFullyCollapsed = fullyCollapsedMaps[0];
  const wrapperStyle = calculateWrapperStyle(fullyCollapsedMaps, variant);

  return (
    <>
      {/* White background wrapper */}
      <div style={wrapperStyle} />

      {/* Thick lines above the wrapper */}
      {fullyCollapsedMaps.map((relative, index) => (
        <FullyCollapsedThickLine
          key={relative.milestoneMap.uuid}
          relative={relative}
          index={index}
          totalMaps={fullyCollapsedMaps.length}
          firstFullyCollapsed={firstFullyCollapsed}
          isHovered={hoveredThickLineUuid === relative.milestoneMap.uuid}
          onMouseEnter={() => setHoveredThickLineUuid(relative.milestoneMap.uuid)}
          onMouseLeave={() => setHoveredThickLineUuid(null)}
          onClick={() => {
            updateMapState?.(relative.milestoneMap.uuid, { collapseLevel: 'titleOnly' });
          }}
          updateMapState={updateMapState}
        />
      ))}
    </>
  );
}
